import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Camera, Microscope, GitCompareArrows, X, ExternalLink, Plus } from "lucide-react";
import { dermaPhotoUrl } from "@/components/DermaPhotos";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—");
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const dayOf = (p) => (p.visit_date || p.created_at || "").slice(0, 10);
const KIND_LABEL = { clinica: "Clinica", dermatoscopica: "Dermatoscopica" };

const daysBetween = (a, b) => {
  const d = Math.round(Math.abs(new Date(dayOf(b)) - new Date(dayOf(a))) / 86400000);
  if (d < 1) return "stesso giorno";
  if (d < 60) return `${d} giorni`;
  const m = Math.round(d / 30.4);
  return m < 24 ? `${m} mesi` : `${(d / 365).toFixed(1)} anni`;
};

const MarkerBadge = ({ n }) => n ? (
  <span className="inline-flex h-5 w-5 rounded-full bg-red-600 text-white text-[10px] font-bold items-center justify-center" title={`Sede ${n} sulla mappa corporea`}>{n}</span>
) : null;

const Tile = ({ p, selected, onCompare, onQuickCompare, hasPrevSameSite, idx }) => (
  <div className={`rounded-md border bg-white p-1.5 ${selected ? "border-blue-900 ring-2 ring-blue-900/30" : "border-slate-200"}`} data-testid={`gallery-tile-${idx}`}>
    <a href={dermaPhotoUrl(p.id)} target="_blank" rel="noreferrer" title="Apri a schermo intero">
      <img src={dermaPhotoUrl(p.id, 320)} alt={p.site || "Foto"} className="w-full h-36 object-cover rounded bg-slate-100" loading="lazy" data-testid={`gallery-img-${idx}`} />
    </a>
    <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-700 min-w-0">
      {p.kind === "dermatoscopica" ? <Microscope className="h-3 w-3 text-blue-900 flex-shrink-0" /> : <Camera className="h-3 w-3 text-blue-900 flex-shrink-0" />}
      <MarkerBadge n={p.body_marker} />
      <span className="truncate font-medium">{p.site || "Sede non indicata"}</span>
    </div>
    {p.note && <div className="text-[10px] text-slate-500 truncate">{p.note}</div>}
    <div className="mt-1.5 flex items-center justify-between gap-1">
      <button type="button" onClick={() => onCompare(p.id)} data-testid={`gallery-compare-${idx}`}
              className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${selected ? "bg-blue-900 text-white border-blue-900" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
        <GitCompareArrows className="h-3 w-3" /> {selected ? "Selezionata" : "Confronta"}
      </button>
      {hasPrevSameSite && (
        <button type="button" onClick={() => onQuickCompare(p)} data-testid={`gallery-quick-compare-${idx}`}
                className="text-[10px] text-blue-900 underline">con precedente</button>
      )}
    </div>
  </div>
);

export const DermaPhotoGallery = ({ fiscalCode }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState("");
  const [kind, setKind] = useState("");
  const [cmp, setCmp] = useState([]);

  useEffect(() => {
    if (!fiscalCode || fiscalCode.length !== 16) { setPhotos([]); setLoading(false); return; }
    setLoading(true);
    api.get("/derma-photos", { params: { patient_fiscal_code: fiscalCode } })
      .then((r) => setPhotos(r.data.items || [])).catch(() => setPhotos([])).finally(() => setLoading(false));
  }, [fiscalCode]);

  const sites = useMemo(() => [...new Set(photos.map((p) => (p.site || "").trim()).filter(Boolean))].sort(), [photos]);
  const filtered = photos.filter((p) => (!site || (p.site || "").trim() === site) && (!kind || p.kind === kind));
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((p) => { const k = dayOf(p); if (!m.has(k)) m.set(k, []); m.get(k).push(p); });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);
  // Meta del giorno (referto emesso / link PDF) calcolata su TUTTE le foto del giorno, non sul sottoinsieme filtrato
  const dayMeta = useMemo(() => {
    const m = {};
    photos.forEach((p) => { const k = dayOf(p); m[k] = m[k] || { total: 0, pdf: null }; m[k].total += 1; if (!m[k].pdf && p.referto_download_url) m[k].pdf = p.referto_download_url; });
    return m;
  }, [photos]);

  const byId = (id) => photos.find((p) => p.id === id);
  const toggleCmp = (id) => setCmp((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c.slice(-1), id]));
  const prevSameSite = (p) => photos.filter((q) => q.id !== p.id && q.kind === p.kind && (q.site || "").trim() && (q.site || "").trim() === (p.site || "").trim() && dayOf(q) < dayOf(p))
    .sort((a, b) => (dayOf(a) < dayOf(b) ? 1 : -1))[0];
  const quickCompare = (p) => { const q = prevSameSite(p); if (q) setCmp([q.id, p.id]); };
  const pair = cmp.map(byId).filter(Boolean).sort((a, b) => (dayOf(a) < dayOf(b) ? -1 : 1));

  if (loading) return <div className="text-sm text-slate-500 py-6 text-center">Caricamento foto…</div>;
  if (!photos.length) return (
    <div className="text-sm text-slate-500 py-6 text-center" data-testid="gallery-empty">
      Nessuna foto dermatologica archiviata per questo paziente.
      <div className="mt-2"><button type="button" onClick={() => navigate(`/nuovo-referto?cf=${encodeURIComponent(fiscalCode || "")}`)} className="text-blue-900 underline text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Nuova visita con foto</button></div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="derma-gallery">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">{photos.length} foto · {groups.length || 1} {groups.length === 1 ? "data" : "date"}</span>
        <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="gallery-filter-site" className="h-7 rounded border border-slate-300 bg-white px-1.5">
          <option value="">Tutte le sedi</option>{sites.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="gallery-filter-kind" className="h-7 rounded border border-slate-300 bg-white px-1.5">
          <option value="">Cliniche + dermatoscopiche</option><option value="clinica">Solo cliniche</option><option value="dermatoscopica">Solo dermatoscopiche</option>
        </select>
        <span className="text-slate-400 ml-auto hidden md:inline">Seleziona due foto con "Confronta" per vederle affiancate</span>
      </div>

      {pair.length === 2 && (
        <div className="rounded-lg border border-blue-900/30 bg-blue-50/40 p-3" data-testid="gallery-compare-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-blue-900 flex items-center gap-1.5"><GitCompareArrows className="h-4 w-4" /> Confronto rapido · intervallo {daysBetween(pair[0], pair[1])}</div>
            <button type="button" onClick={() => setCmp([])} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="gallery-compare-close"><X className="h-3.5 w-3.5" /> Chiudi</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {pair.map((p, i) => (
              <div key={p.id} className="min-w-0">
                <div className="text-[11px] font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-white ${i === 0 ? "bg-slate-500" : "bg-blue-900"}`}>{i === 0 ? "PRIMA" : "DOPO"}</span>
                  {fmtShort(p.visit_date || p.created_at)} · {KIND_LABEL[p.kind] || "Foto"} <MarkerBadge n={p.body_marker} /> {p.site && <span className="truncate">· {p.site}</span>}
                </div>
                <a href={dermaPhotoUrl(p.id)} target="_blank" rel="noreferrer">
                  <img src={dermaPhotoUrl(p.id)} alt="" className="w-full h-64 md:h-80 object-contain rounded border border-slate-300 bg-white" data-testid={`gallery-compare-img-${i}`} />
                </a>
                {p.note && <div className="text-[11px] text-slate-600 mt-1">{p.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <ul className="relative space-y-3 before:absolute before:left-[13px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200" data-testid="gallery-timeline">
        {groups.map(([day, list], gi) => (
          <li key={day} className="relative pl-8" data-testid={`gallery-day-${gi}`}>
            <span className="absolute left-[7px] top-3 h-3.5 w-3.5 rounded-full ring-4 ring-white bg-blue-900" />
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2 mb-2 text-sm">
                <span className="font-semibold text-slate-900">{fmtDate(day)}</span>
                <span className="text-xs text-slate-500">{list.length}{dayMeta[day]?.total > list.length ? ` di ${dayMeta[day].total}` : ""} foto{dayMeta[day]?.pdf ? " · referto emesso" : " · visita in corso / bozza"}</span>
                {dayMeta[day]?.pdf && (
                  <a href={dayMeta[day].pdf} target="_blank" rel="noreferrer" className="text-[11px] text-blue-900 underline flex items-center gap-1 ml-auto" data-testid={`gallery-day-pdf-${gi}`}><ExternalLink className="h-3 w-3" /> PDF referto</a>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {list.map((p, i) => (
                  <Tile key={p.id} p={p} idx={`${gi}-${i}`} selected={cmp.includes(p.id)} onCompare={toggleCmp} onQuickCompare={quickCompare} hasPrevSameSite={!!prevSameSite(p)} />
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
